import React, { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { NotificationIcon, CloseIcon, ArrowRightIcon } from "../../icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const NotificationsTrigger: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");

  const handleTabChange = (newTab: string) => {
    setTab(newTab);
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "critical":
        return "bg-red-500";
      case "warning":
        return "bg-orange-500";
      case "info":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  // Mock data - replace with actual data later
  const notifications = [
    {
      id: 1,
      type: "info",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: false,
    },
    {
      id: 2,
      type: "critical",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: false,
    },
    {
      id: 3,
      type: "warning",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: true,
    },
    {
      id: 4,
      type: "info",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: false,
    },
    {
      id: 5,
      type: "critical",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: false,
    },
    {
      id: 6,
      type: "warning",
      title: "Notification Title",
      message: "Wheel: W001-L, Bogie ID: B001, Wagon: wagon-00, On Unit: TRN-001 Possible flat spot.",
      time: "19:02",
      date: "15.May.2025",
      read: true,
    },
  ];

  // Filter notifications based on selected tab
  const filteredNotifications = tab === "all"
    ? notifications
    : notifications.filter(notification => notification.type === tab);

  // Count notifications by type
  const getCounts = () => {
    const criticalCount = notifications.filter(n => n.type === "critical").length;
    const warningCount = notifications.filter(n => n.type === "warning").length;
    const infoCount = notifications.filter(n => n.type === "info").length;
    return { criticalCount, warningCount, infoCount };
  };

  const { criticalCount, warningCount } = getCounts();

  const isLoading = false;
  const hasNotifications = filteredNotifications.length > 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          data-open={open}
          className="group my-3 flex cursor-pointer items-center gap-3 text-neutral-300"
        >
          <div className="relative">
            <NotificationIcon className="w-4 group-data-[open=true]:text-white" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
          </div>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={20}
          alignOffset={-16}
          className="z-[9999] w-[390px] overflow-hidden rounded-md border-[0.4px] border-[#E4E4E4] bg-[#203947] text-[14px] text-neutral-50"
        >
          <header className="relative py-3 text-center text-lg">
            {t("navbar.notifications")}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-[18px] right-5 cursor-pointer"
            >
              <CloseIcon className="w-4" />
            </button>
          </header>

          <div className="flex gap-4 bg-[#1A1F2E] px-4 pt-4  justify-between">
            {[
              { label: t("navbar.all"), type: "all", showCount: false, count: 0 },
              { label: t("navbar.criticalAlerts"), type: "critical", showCount: true, count: criticalCount },
              { label: t("navbar.warning"), type: "warning", showCount: true, count: warningCount },
              { label: t("navbar.info"), type: "info", showCount: false, count: 0 },
            ].map((item) => (
              <button
                key={item.label}
                data-active={tab === item.type}
                data-type={item.type}
                onClick={() => handleTabChange(item.type as any)}
                className="group flex cursor-pointer items-center gap-2 border-neutral-50 pb-2 text-sm font-medium text-neutral-300 data-[active=true]:text-neutral-50 data-[active=true]:border-b-[1.4px] data-[active=true]:font-bold"
              >
                {item.label}
                {item.showCount && item.count > 0 && (
                  <span
                    className={`
                      flex items-center justify-center text-xs font-bold text-white min-w-[20px] h-5 px-1 rounded-full
                      ${item.type === "critical" ? "bg-red-500" :
                        item.type === "warning" ? "bg-orange-500" :
                          "bg-gray-500"}
                    `}
                  >
                    {item.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="max-h-[64vh] overflow-y-auto pt-4">
            <section className="grid">
              {filteredNotifications?.map((item, index) => (
                <div
                  key={item.id}
                  className="flex flex-row gap-3 w-full bg-[#15212F] border-b-1 border-[#274654] p-4"
                  style={{
                    backgroundColor: index % 2 === 0 ? "#15212F" : "#182836",
                  }}
                >
                  <div className="flex-shrink-0">
                    <div className={`w-3 h-3 rounded-full mt-1 ${getNotificationColor(item.type)}`}></div>
                  </div>

                  <div className="flex flex-col gap-1 flex-1">
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-white">{item.title}</span>
                        <span className="text-xs text-white">{item.message}</span>
                      </div>
                      <div className="flex flex-col items-end text-right">
                        <span className="text-gray-400 text-xs" >
                          {item.time}
                        </span>
                        <span className="text-gray-400 text-xs" >
                          {item.date}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center mt-2 justify-end">
                      <span className="text-[#1DE4D4] font-semibold text-xs cursor-pointer hover:text-gray-300 transition-colors">
                        Details
                      </span>
                      <span className="text-[#1DE4D4] text-xl ml-1">›</span>
                    </div>
                  </div>
                </div>
              ))}

              {isLoading &&
                !hasNotifications &&
                [...Array(10)].map((_, index) => (
                  <div
                    key={`loading-initial-${index}`}
                    className="p-4 border-b border-gray-700 animate-pulse"
                  >
                    <div className="h-4 bg-gray-600 rounded mb-2"></div>
                    <div className="h-3 bg-gray-600 rounded w-3/4"></div>
                  </div>
                ))}
            </section>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="flex w-full items-center justify-between gap-2 bg-[#242F46] px-[22px] py-[13px] text-white"
          >
            <span>{t("navbar.seeAllNotifications")}</span>
            <ArrowRightIcon className="h-[12px] w-[7.2px] scale-110" />
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
